import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import {
  Button,
  Drawer,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material'
import dayjs from 'dayjs'
import { useState } from 'react'
import { ALL_GENDERS, EVENT_TYPES_ARRAY, MOMENT_FORMATS } from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { getOrdinalSuffix, isValidNumber } from 'src/consumer/helpers/utilFunctions'
import { getPriceStringWithCurrency } from 'src/mentor/helpers/utilityFunctions'
import FeeModelViewDetails from './FeeModelViewDetails'
import PayoutDistributionReportModelDetails from './PayoutDistributionReportModelDetails'
import { putSPDataWithParams } from 'src/supportPortal/services/services'
import { toast } from 'react-toastify'
import ReportPaymentDetails from 'src/clinic/views/clinicBrandPayoutReports/components/ReportPaymentDetails/ReportPaymentDetails'
import ReconciliationDialog from './ReconciliationDialog'
import { checkIfReportNeedsReconsiliationForTherapists } from 'src/mentor/components/TeacherPayoutReports/utils'

const DRAWER_WIDTH = 420
const DRAWER_VARIANTS = { SESSION: 'session', MODEL: 'model', PAYMENT: 'payment', RECONCILE: 'reconcile' }

const ActionButton = ({ children, onClick, color }) => (
  <Button
    size="small"
    onClick={onClick}
    sx={{
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.8125rem',
      minWidth: 'auto',
      px: 1,
      py: 0.5,
      color: color || 'primary.main',
      justifyContent: 'flex-start',
    }}
  >
    {children}
  </Button>
)

export default function ClinicBrandPayoutReportsViewDetails({
  report,
  forMentor = false,
  hideFeeDetailsWhenShareZero = false,
  showAdminActions = false,
  refreshReports = () => {},
  showBookingLogsButton = false,
  openBookedEventLogsDialog = () => {},
}) {
  const theme = useTheme()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerVariant, setDrawerVariant] = useState(null)

  async function markPatientAsTest() {
    const toastId = toast.loading('Marking patient as test...')
    try {
      const response = await putSPDataWithParams(
        '/api/secure/cc/support/priv/patient/test-patient/mark',
        null,
        {
          'patient-id': report?.patientId,
        },
      )
      if (response?.status === 200) {
        toast.update(toastId, {
          render: 'Patient marked as test successfully',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        })
        refreshReports()
      } else {
        toast.update(toastId, {
          render: 'Failed to mark patient as test',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        })
      }
    } catch (error) {
      toast.update(toastId, {
        render: 'Error marking patient as test',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      })
    }
  }

  async function unmarkPatientAsTest() {
    const toastId = toast.loading('Unmarking patient as test...')
    try {
      const response = await putSPDataWithParams(
        '/api/secure/cc/support/priv/patient/test-patient/unmark',
        null,
        {
          'patient-id': report?.patientId,
        },
      )
      if (response?.status === 200) {
        toast.update(toastId, {
          render: 'Patient unmarked as test successfully',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        })
        refreshReports()
      } else {
        toast.update(toastId, {
          render: 'Failed to unmark patient as test',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        })
      }
    } catch (error) {
      toast.update(toastId, {
        render: 'Error unmarking patient as test',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      })
    }
  }

  function openDrawer(variant) {
    setDrawerVariant(variant)
    setDrawerOpen(true)
  }

  function closeDrawer() {
    setDrawerOpen(false)
    setDrawerVariant(null)
  }

  function handleReconciliationSuccess(updatedBookedEventInfo) {
    refreshReports()
    closeDrawer()
  }

  const shareAmt = forMentor ? report?.mentorShare : report?.sourceShare

  let shareIsZero = isValidNumber(shareAmt) && shareAmt === 0
  const needsReconcile = checkIfReportNeedsReconsiliationForTherapists(report)
  const entityFeeModel = forMentor ? report?.mentorEntityFeeModelUsed : report?.sourceEntityFeeModelUsed
  const feeModel = forMentor ? report?.mentorFeeModelUsed : report?.sourceFeeModelUsed

  const drawerTitles = {
    [DRAWER_VARIANTS.SESSION]: 'Session details',
    [DRAWER_VARIANTS.MODEL]: 'Collection model',
    [DRAWER_VARIANTS.PAYMENT]: 'Payment details',
    [DRAWER_VARIANTS.RECONCILE]: 'Reconcile',
  }

  return (
    <>
    <span
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'row',
        gap: theme.spacing(0.5),
        alignItems: 'center',
        paddingTop: theme.spacing(0.25),
        paddingBottom: theme.spacing(0.25),
      }}
    >
      <ActionButton onClick={() => openDrawer(DRAWER_VARIANTS.SESSION)}>Session</ActionButton>
      <PayoutDistributionReportModelDetails
        report={report}
        getSharePercent={(report) =>
          forMentor ? report?.mentorSharePercentage : report?.sourceSharePercentage
        }
        getShareAmount={(report) => shareAmt}
        getEntityTitle={(report) => (forMentor ? report?.mentorName : report?.sourceName)}
        getEntityFeeModelDetails={(report) =>
          forMentor ? report?.mentorEntityFeeModelUsed : report?.sourceEntityFeeModelUsed
        }
        hide
        getFeeModelDetails={(report) =>
          forMentor ? report?.mentorFeeModelUsed : report?.sourceFeeModelUsed
        }
        getCurrencyCode={(report) => report?.currency}
        hideAllTableSummaryDetails
        forMentor={forMentor}
        viewButton={() => <ActionButton onClick={() => openDrawer(DRAWER_VARIANTS.MODEL)}>Model</ActionButton>}
      />
      {showAdminActions && (
        <>
          <ActionButton onClick={report?.testPatient ? unmarkPatientAsTest : markPatientAsTest}>
            {report?.testPatient ? 'Unmark test' : 'Mark test'}
          </ActionButton>
          <ActionButton onClick={() => openDrawer(DRAWER_VARIANTS.PAYMENT)}>Payment</ActionButton>
          {needsReconcile && (
            <ActionButton onClick={() => openDrawer(DRAWER_VARIANTS.RECONCILE)} color="error.main">
              Reconcile
            </ActionButton>
          )}
        </>
      )}
      {openBookedEventLogsDialog && (
        <ActionButton onClick={() => openBookedEventLogsDialog(report)}>Event Logs</ActionButton>
      )}
    </span>
    <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.2)' } } }}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: DRAWER_WIDTH },
            maxWidth: '100%',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          },
        }}
      >
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
              paddingTop: theme.spacing(1.5),
              paddingBottom: theme.spacing(1.5),
              borderBottom: `1px solid ${theme.palette.divider}`,
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              {drawerVariant ? drawerTitles[drawerVariant] : ''}
            </Typography>
            <IconButton onClick={closeDrawer} size="small" aria-label="Close drawer">
              <CloseRoundedIcon />
            </IconButton>
          </div>
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(2),
            }}
          >
            {drawerVariant === DRAWER_VARIANTS.SESSION && (
              <SessionDrawerContent
                report={report}
                hideFeeDetailsWhenShareZero={hideFeeDetailsWhenShareZero}
                shareIsZero={shareIsZero}
              />
            )}
            {drawerVariant === DRAWER_VARIANTS.MODEL && entityFeeModel && feeModel && (
              <FeeModelViewDetails
                entityFeeModel={entityFeeModel}
                feeModel={feeModel}
                forMentor={forMentor}
                closeDialog={closeDrawer}
                renderInDrawer
              />
            )}
            {drawerVariant === DRAWER_VARIANTS.PAYMENT && (
              <ReportPaymentDetails reportItem={report} embedded />
            )}
            {drawerVariant === DRAWER_VARIANTS.RECONCILE && (
              <ReconciliationDialog
                open
                onClose={closeDrawer}
                report={report}
                onSuccess={handleReconciliationSuccess}
                renderInDrawer
              />
            )}
          </div>
        </div>
      </Drawer>
    </>
  )
}

function SessionDrawerContent({ report, hideFeeDetailsWhenShareZero }) {
  const theme = useTheme()
  const foundEventType = EVENT_TYPES_ARRAY.find((eType) => eType.id === report?.eventType)
  const genderTitle = ALL_GENDERS.find((gndr) => gndr?.id === report?.patientGender)?.title || report?.patientGender
  const ageStr = getAgeFromDobWithTime(report?.patientDateOfBirth)
  const collectedAtStr = report?.collectedAt
    ? dayjs.utc(report.collectedAt).local().format(MOMENT_FORMATS.completeTimeDetailsWithMonthShortForm)
    : '—'
  const startStr = report?.bookedEventStartTime
    ? dayjs.utc(report.bookedEventStartTime).local().format(MOMENT_FORMATS.completeTimeDetailsWithMonthShortForm)
    : '—'
  const endStr = report?.bookedEventEndTime
    ? dayjs.utc(report.bookedEventEndTime).local().format(MOMENT_FORMATS.completeTimeDetailsWithMonthShortForm)
    : '—'
  const nextVisitStr = report?.nextVisitDate
    ? dayjs.utc(report.nextVisitDate).local().format(MOMENT_FORMATS.completeDateDetailsWithMonthShortForm)
    : '—'
  const feeStr = !hideFeeDetailsWhenShareZero
    ? getPriceStringWithCurrency(report?.feeCollected, report?.currency)
    : '—'

  const Row = ({ label, value }) => (
    <div
      style={{
        paddingTop: theme.spacing(1.25),
        paddingBottom: theme.spacing(1.25),
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value ?? '—'}
      </Typography>
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Patient
      </Typography>
      <Row label="Name" value={`${report?.patientName ?? '—'} (${genderTitle ?? '—'} / ${ageStr ?? '—'})`} />
      <Row label="Patient number" value={report?.patientNumber} />

      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
        Fee & collection
      </Typography>
      <Row label="Fee collected" value={feeStr} />
      <Row label="Collected at" value={collectedAtStr} />
      <Row label="Collected by" value={report?.collectedBy} />
      <Row label="Visit" value={getOrdinalSuffix(report?.sessionNumOfSameTypeWithSameMentor)} />

      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
        Booking
      </Typography>
      <Row label="Location" value={`${report?.sourceName || 'Clinic'} ${report?.clinicLocationName || ''}`.trim()} />
      <Row label={foundEventType?.mentorNoun ?? 'Provider'} value={report?.mentorName} />
      <Row label="Start time" value={startStr} />
      <Row label="End time" value={endStr} />
      <Row label="Next visit" value={nextVisitStr} />
    </div>
  )
}

// Session Details
// Visit Number:	3rd
// Booked By	Sharad Oberoi
// Booking Time:	4 January 2025, 15:30
// Booked At:	Ashirward Ambedkarnagar Clinic

// Therapist:	Sampada Vijayvargiya
// Start Time:
// End Time:

// Next Date:
